import React, { useState, useImperativeHandle, forwardRef, useEffect } from "react";
import {
  ThemeProvider,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableContainer,
  TableBody,
  Typography,
  SxProps,
  Box,
  Pagination,
  Button,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InboxIcon from "@mui/icons-material/Inbox";
import { EmptyState, EmptyStateProps } from "../emptyState/DtEmptyState"; // adjust path if needed
import colorTheme from "../../../assets/colorTheme";
import { IDataColumn, DataGridActionConfig, DTDataGridAlignment } from "./dtDataGridTypes";
import DtDataGridCell from "./DtDataGridCell";
import DtDataGridActionsCell from "./DtDataGridActionsCell";
import { DtConfirmDialog } from "../dialog/DtConfirmDialog";
import { DtLoader } from "../loader/DtLoader"; // Add this import
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";

export interface AddConfig {
  label?: string;
  icon?: React.ReactNode;
  mode?: "inline" | "custom"; // Add mode: inline or custom
  onAdd?: (row: any, mode: "inline" | "custom") => void;
  // You can add more add-row related config here
}

export interface EditConfig {
  label?: string;
  icon?: React.ReactNode;
  mode?: "inline" | "custom";
  onEdit?: (row: any, rowIndex: number, mode: "inline" | "custom") => void;
  // You can add more edit-related config here
}

export interface DeleteConfig {
  label?: string;
  icon?: React.ReactNode;
  confirm?: boolean;
  confirmTitle?: string;
  confirmMessage?: string;
  confirmYesLabel?: string;
  confirmNoLabel?: string;
  onDelete?: (row: any, rowIndex: number) => void;
}

export interface ViewConfig {
  label?: string;
  icon?: React.ReactNode;
  mode?: "inline" | "custom";
  onView?: (row: any, rowIndex: number, mode: "inline" | "custom") => void;
  // You can add more view-related config here
}

interface ExpanderConfig {
  expanderIcon?: React.ReactNode; // Custom icon for expand/collapse
  expanderTooltip?: string | ((expanded: boolean, row: any, rowIndex: number) => string); // <-- Add this
  onRowExpand?: (row: any, rowIndex: number, expanded: boolean) => void;
  renderExpandedRow?: (row: any, rowIndex: number) => React.ReactNode;
  expanderColumnWidth?: number; // Optional: width of expander column
}

interface DataGridProps {
  columns: Array<IDataColumn>;
  data: Array<any>;
  tableName?: string;
  containerSx?: SxProps;
  dense?: boolean;
  paged?: boolean;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  loading?: boolean;
  actionsConfig?: DataGridActionConfig;
  addConfig?: AddConfig;
  editConfig?: EditConfig;
  deleteConfig?: DeleteConfig;
  viewConfig?: ViewConfig; // <-- Add this line
  onRowSelect?: (row: any, rowIndex: number) => void;
  connectorBetweenColumns?: Array<{
    from: number;
    to: number;
    connector?: React.ReactNode;
  }>;
  highlightSelectedRow?: boolean;
  responsivePageSize?: boolean;
  responsivePageConfig?: {
    heightRatio?: number;
    rowHeight?: number;
    reservedHeight?: number;
  };
  expanderConfig?: ExpanderConfig;
  emptyState?: Partial<EmptyStateProps>; 
}

const getResponsivePageSize = (config?: { heightRatio?: number; rowHeight?: number; reservedHeight?: number }) => {
  const heightRatio = config?.heightRatio ?? 0.85;
  const rowHeight = config?.rowHeight ?? 48;
  const reservedHeight = config?.reservedHeight ?? 100;
  const availableHeight = window.innerHeight * heightRatio;
  const usableHeight = Math.max(availableHeight - reservedHeight, rowHeight);
  const rows = Math.floor(usableHeight / rowHeight);
  if (rows < 5) return 5;
  if (rows > 50) return 50;
  return rows;
};

const DtDataGrid = forwardRef<any, DataGridProps>((props, ref) => {
  const theme = useTheme();
  const [editRowIndex, setEditRowIndex] = useState<number | null>(null);
  const [editRowData, setEditRowData] = useState<any | null>(null);
  const [addMode, setAddMode] = useState<boolean>(false);
   const [currPage, setCurrPage] = useState(props.currentPage ?? 1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ row: any; rowIndex: number } | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null); // Track expanded row
  const [gotoPage, setGotoPage] = useState<number | "">(currPage);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Responsive page size logic
  const defaultPageSize = props.responsivePageSize
    ? getResponsivePageSize(props.responsivePageConfig)
    : props.pageSize ?? 5;

  const pageSizeOptions = Array.from(new Set([5, 10, 15, 20, 50, 100, defaultPageSize])).sort((a, b) => a - b);

  const [rowsPerPage, setRowsPerPage] = useState(defaultPageSize);

  useEffect(() => {
    if (props.currentPage) {
      setCurrPage(props.currentPage);
    }
  }, [props.currentPage]);

  // Listen to window resize and update rowsPerPage if pageSize prop is not set
  useEffect(() => {
    if (!props.responsivePageSize) return;
    const handleResize = () => {
      setRowsPerPage(getResponsivePageSize(props.responsivePageConfig));
      setRowsPerPage(getResponsivePageSize(props.responsivePageConfig));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [props.responsivePageSize, props.responsivePageConfig]);

  // Sorting (optional, can be modularized)
  const [sortedCol, setSortedCol] = useState(-1);
  const [isSorted, setIsSorted] = useState(false);
  const [sortDirs, setSortDirs] = useState<Array<boolean>>([]);

  const setColumnSort = (index: number) => {
    let arsort: Array<boolean> = [];
    if (!sortDirs.length) {
      props.columns?.forEach(() => arsort.push(false));
    } else {
      arsort = [...sortDirs];
    }
    arsort[index] = !arsort[index];
    setSortDirs([...arsort]);
    setSortedCol(index);
    setIsSorted(true);
  };

  const getSortedItems = () => {
    let ret = props.data;
    if (isSorted && sortedCol > -1) {
      const sortCol = props.columns[sortedCol].fieldId;
      const sortOrd = sortDirs[sortedCol];
      ret = [...props.data].sort((a, b) => {
        let v1 = a[sortCol];
        let v2 = b[sortCol];
        if (typeof v1 === "string" && typeof v2 === "string") {
          v1 = v1.toLowerCase();
          v2 = v2.toLowerCase();
        }
        return (v1 < v2 ? -1 : v1 > v2 ? 1 : 0) * (sortOrd ? 1 : -1);
      });
    }
    if (props.paged) {
      return ret.slice((currPage - 1) * rowsPerPage, currPage * rowsPerPage);
    }
    return ret;
  };

  // --- Validation Helper ---
  const validateRow = (row: any) => {
    const errors: Record<string, string> = {};
    props.columns.forEach(col => {
      const value = row[col.fieldId];
      if (col.required && (value === "" || value === undefined || value === null || (col.datatype === "checkbox" && value === false))) {
        errors[col.fieldId] = "This field is required";
      } else if (col.validate && !col.validate(value, row)) {
        errors[col.fieldId] = "Invalid value";
      }
    });
    return errors;
  };

  // --- Add Row ---
  const renderAddRow = () => {
    if (!props.addConfig) return null;
    const addModeType = props.addConfig.mode || "inline";
    if (addMode && addModeType === "inline") {
      const emptyRow: any = {};
      props.columns.forEach((col) => {
        emptyRow[col.fieldId] = col.datatype === "checkbox" ? false : "";
      });
      return (
        <TableRow>
          {props.columns.map((col, cidx) => (
            <>
              <DtDataGridCell
                key={cidx}
                row={editRowData || emptyRow}
                col={col}
                rowIndex={0}
                colIndex={cidx}
                isEditing={true}
                editRowData={editRowData || emptyRow}
                setEditRowData={setEditRowData}
                controlWidth={col.controlWidth}
                errors={validationErrors}
                sx={
                  col.sx ?? {}
                }
              />
              {shouldRenderConnector(cidx) && (
                <TableCell
                  key={`connector-cell-${editRowData || emptyRow}-${cidx}`}
                  align="center"
                  sx={{ width: 32, minWidth: 32, border: 0, background: "transparent", p: 0 }}
                >
                  {props.connectorBetweenColumns?.find(conn => conn.from === cidx)?.connector ?? <ArrowForwardIcon color="action" />}
                  {props.connectorBetweenColumns?.find(conn => conn.from === cidx)?.connector ?? <ArrowForwardIcon color="action" />}
                </TableCell>
              )}</>
          ))}
          <DtDataGridActionsCell
            actionsConfig={props.actionsConfig}
            row={editRowData || emptyRow}
            rowIndex={0}
            isEditing={false}
            addMode={true}
            editRowData={editRowData || emptyRow}
            onSave={() => {
              const errors = validateRow(editRowData || emptyRow);
              setValidationErrors(errors);
              if (Object.keys(errors).length > 0) return; // Prevent save if errors
              if (props.addConfig && typeof props.addConfig.onAdd === "function") {
                props.addConfig.onAdd(editRowData, "inline");
                setTimeout(() => {
                  const totalRows = (props.data?.length ?? 0) + 1;
                  setCurrPage(Math.ceil(totalRows / rowsPerPage));
                }, 0);
              }
              setAddMode(false);
              setEditRowData(null);
              setValidationErrors({});
            }}
            onCancel={() => {
              setAddMode(false);
              setEditRowData(null);
              setValidationErrors({});
            }}
          />
        </TableRow>
      );
    }
    return (
      <TableRow>
        <TableCell
          colSpan={
            props.columns.length + (props.actionsConfig && props.actionsConfig.actions.length > 0 ? 1 : 0)
          }
          align="right"
        >
          <Button
            color="primary"
            size="small"
            onClick={() => {
              if (addModeType === "custom" && props.addConfig?.onAdd) {
                props.addConfig.onAdd({}, "custom");
              } else {
                setAddMode(true);
                const emptyRow: any = {};
                props.columns.forEach((col) => {
                  emptyRow[col.fieldId] = col.datatype === "checkbox" ? false : "";
                });
                setEditRowData(emptyRow);
              }
            }}
            sx={{ padding: "3px 8px" }}
          >
            <AddIcon fontSize="small" />
            <span style={{ marginLeft: 8, padding: 0 }}>
              {props.addConfig?.label || "Add Row"}
            </span>
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  const standardFontSize = 16;

  // Helper to determine if a connector should be rendered after a column
  const shouldRenderConnector = (colIdx: number) => {
    if (!props.connectorBetweenColumns || !Array.isArray(props.connectorBetweenColumns)) return false;
    return props.connectorBetweenColumns.some(conn => conn.from === colIdx);
  };

  // --- Render Table Header Cell ---
  function getTableHeaderCell(col: IDataColumn, index: number) {
    const widthStyle: React.CSSProperties = col.width
      ? { width: `${Math.min(col.width, 100)}%`, boxSizing: "border-box" as const, fontSize: standardFontSize }
      : { boxSizing: "border-box" as const, fontSize: standardFontSize };
    const align: "left" | "right" | "center" =
      (col.alignment as DTDataGridAlignment) || (col.datatype === "checkbox" ? "center" : "left");

    // Add sort icon and click handler if sortable
    const isSortable = col.sortable !== false; // default true
    const isActive = sortedCol === index;
    const sortDir = sortDirs[index];

    return (
      <React.Fragment key={index}>
        <TableCell
          component="th"
          scope="col"
          key={index}
          style={widthStyle}
          align={align}
          sx={{
            cursor: isSortable ? "pointer" : "default",
            userSelect: "none",
            verticalAlign: "middle"
          }}
          onClick={isSortable ? () => setColumnSort(index) : undefined}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <span style={{ fontWeight: "bold", fontSize: standardFontSize, opacity: 0.8 }}>
              {col.name}
            </span>
            {isSortable && (
              <Box sx={{ display: "inline-flex", flexDirection: "column", ml: 0.5 }}>
                {isActive ? (
                  sortDir ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowUpwardIcon fontSize="small" sx={{ opacity: 0.2 }} />
                )}
              </Box>
            )}
          </Box>
        </TableCell>
        {shouldRenderConnector(index) && (
          <TableCell
            key={`connector-header-${index}`}
            align="center"
            sx={{ width: 32, minWidth: 32, border: 0, background: "transparent", p: 0 }}
          >
            <></>
            <></>
          </TableCell>
        )}
      </React.Fragment>
    );
  }

  // --- Render Empty State ---
  const renderEmptyState = () => (
    <TableRow>
      <TableCell
        colSpan={props.columns.length + (props.actionsConfig && props.actionsConfig.actions.length > 0 ? 1 : 0)}
        align="center"
        sx={{ p: 0, border: 0 }}
      >
        <EmptyState
          icon={<InboxIcon sx={{ fontSize: 48, color: "text.secondary" }} />}
          header="No data available"
          subheader="There are currently no records to display."
          action={
            props.addConfig
              ? () => {
                if (props.addConfig && props.addConfig.mode === "custom" && props.addConfig.onAdd) {
                  props.addConfig.onAdd({}, "custom");
                } else {
                  setAddMode(true);
                  const emptyRow: any = {};
                  props.columns.forEach((col) => {
                    emptyRow[col.fieldId] = col.datatype === "checkbox" ? false : "";
                  });
                  setEditRowData(emptyRow);
                }
              }
              : undefined
          }
          actionLabel={props.addConfig?.label || "Add Row"}
          {...props.emptyState}
        />
      </TableCell>
    </TableRow>
  );

  // --- Render Loading State ---
  const renderLoadingState = () => (
    <TableBody>
      <TableRow>
        <TableCell
          colSpan={props.columns.length + (props.actionsConfig && props.actionsConfig.actions.length > 0 ? 1 : 0)}
          sx={{
            p: 0,
            border: 0,
            height: 300, // Match the height of your empty state for visual consistency
          }}
          style={{ padding: 0, border: 0, height: 300 }}
        >
          <Box
            sx={{
              width: "100%",
              height: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DtLoader message="Loading data..." />
          </Box>
        </TableCell>
      </TableRow>
    </TableBody>
  );

  // --- Edit Handler ---
  const handleEditInit = (row: any, rowIndex: number) => {
    if (props.editConfig?.mode === "custom" && typeof props.editConfig.onEdit === "function") {
      props.editConfig.onEdit(row, rowIndex, "custom");
    } else {
      setEditRowIndex(rowIndex);
      setEditRowData({ ...row });
      setValidationErrors({});
    }
  };

  // --- Delete Handler ---
  const handleDelete = (row: any, rowIndex: number) => {
    if (props.deleteConfig?.confirm) {
      setDeleteTarget({ row, rowIndex });
      setDeleteDialogOpen(true);
    } else if (typeof props.deleteConfig?.onDelete === "function") {
      props.deleteConfig.onDelete(row, rowIndex);
      setTimeout(() => {
        // After deletion, check if current page is empty and go to previous page if needed
        const totalRows = props.data.length - 1; // -1 because row is being deleted
        const newPageCount = Math.ceil(totalRows / rowsPerPage);
        if (currPage > newPageCount && newPageCount > 0) {
          setCurrPage(newPageCount);
        }
      }, 0);
    }
  };

  // --- Pagination Controls ---
  useImperativeHandle(ref, () => ({
    moveToLastPage: () => {
      if (!props.paged) return;
      const totalRows = props.data?.length ?? 0;
      const lastPage = Math.max(1, Math.ceil(totalRows / rowsPerPage));
      setCurrPage(lastPage);
    }
  }));

  // Build actions from configs
  function mergeActions(actionsConfigActions: any[], builtActions: any[]) {
    // Remove any actions from builtActions that have the same type as actionsConfigActions
    const actionTypes = new Set(actionsConfigActions.map(a => a.type));
    const filteredBuilt = builtActions.filter(a => !actionTypes.has(a.type));
    // Return actionsConfig actions first, then builtActions (edit, delete)
    return [...actionsConfigActions, ...filteredBuilt];
  }

  const builtActions: any[] = [];
  if (props.viewConfig && typeof props.viewConfig.onView === "function") {
    builtActions.push({
      type: "view",
      icon: props.viewConfig.icon,
      label: props.viewConfig.label,
      onClick: (row: any, rowIndex: number) => {
        props.viewConfig?.onView?.(row, rowIndex, props.viewConfig.mode || "inline");
      }
    });
  }
  if (props.editConfig && typeof props.editConfig.onEdit === "function") {
    builtActions.push({
      type: "edit",
      icon: props.editConfig.icon,
      label: props.editConfig.label,
      onClick: (row: any, rowIndex: number) => {
        props.editConfig?.onEdit?.(row, rowIndex, props.editConfig.mode || "inline");
      }
    });
  }
  if (props.deleteConfig && typeof props.deleteConfig.onDelete === "function") {
    builtActions.push({
      type: "delete",
      icon: props.deleteConfig.icon,
      label: props.deleteConfig.label,
      confirm: props.deleteConfig.confirm,
      confirmTitle: props.deleteConfig.confirmTitle,
      confirmMessage: props.deleteConfig.confirmMessage,
      confirmYesLabel: props.deleteConfig.confirmYesLabel,
      confirmNoLabel: props.deleteConfig.confirmNoLabel,
      onClick: (row: any, rowIndex: number) => {
        if (props.deleteConfig?.confirm) {
          setDeleteTarget({ row, rowIndex });
          setDeleteDialogOpen(true);
        } else {
          props.deleteConfig?.onDelete?.(row, rowIndex) ?? handleDelete(row, rowIndex);
        }
      }
    });
  }

  const mergedActionsConfig = {
    ...(props.actionsConfig || {}),
    actions: mergeActions(props.actionsConfig?.actions || [], builtActions),
    mode: props.actionsConfig?.mode || "inline"
  };

  return (
    <ThemeProvider theme={colorTheme}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <TableContainer
          component="main"
          sx={props.containerSx ?? {
            "& .MuiTableCell-root": {
              px: 2,
              py: 0.8,
            }
          }}
        >
        <Table stickyHeader size={props.dense ? "small" : "medium"} sx={{ tableLayout: "fixed" }}>
          {/* Table Head */}
          {!props.loading && (getSortedItems().length > 0 || (addMode && props.addConfig?.mode === "inline")) && (
            <TableHead>
              <TableRow>
                {props.expanderConfig && (
                  <TableCell key="expander-header" sx={{ width: props.expanderConfig.expanderColumnWidth ?? 40, minWidth: 32, border: 0, background: "transparent", p: 0 }} />
                )}
                {props.columns.map((col, i) => getTableHeaderCell(col, i))}
                {mergedActionsConfig.actions && mergedActionsConfig.actions.length > 0 && (
                  <TableCell
                    align="center"
                    style={{
                      width: mergedActionsConfig.mode === "inline"
                        ? Math.max(mergedActionsConfig.actions.length * 40, 60)
                        : 60,
                      fontWeight: "bold"
                    }}
                  >
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
          )}
          {/* Table Body */}
          {props.loading
            ? renderLoadingState()
            : (
              <TableBody>
                {getSortedItems().length === 0 && !(addMode && props.addConfig?.mode === "inline")
                  ? renderEmptyState()
                  : (
                    <>
                      {getSortedItems().map((row: any, rowIndex: number) => {
                        const isEditing = editRowIndex === rowIndex;
                        const isExpanded = expandedRowIndex === rowIndex;
                        const pageConfig = { page: props.paged ? currPage - 1 : 0, pageSize: rowsPerPage };

                        return (
                          <React.Fragment key={rowIndex}>
                            <TableRow
                              hover
                              key={rowIndex}
                              onClick={() => {
                                if (props.highlightSelectedRow) setSelectedRowIndex(rowIndex);
                                if (typeof props.onRowSelect === "function") props.onRowSelect(row, rowIndex);
                              }}
                              selected={props.highlightSelectedRow ? selectedRowIndex === rowIndex : false}
                              sx={{
                                ...(props.highlightSelectedRow && selectedRowIndex === rowIndex
                                  ? { backgroundColor: theme.palette.action.selected, transition: "background 0.2s" }
                                  : {}),
                                cursor: props.highlightSelectedRow || props.onRowSelect ? "pointer" : undefined,
                              }}
                            >
                              {props.expanderConfig && (
                                <TableCell
                                  key={`expander-cell-${rowIndex}`}
                                  align="center"
                                  sx={{ width: props.expanderConfig.expanderColumnWidth ?? 40, minWidth: 32, border: 0, background: "transparent", p: 0 }}
                                >
                                  <Tooltip
                                    title={typeof props.expanderConfig.expanderTooltip === "function"
                                      ? props.expanderConfig.expanderTooltip(isExpanded, row, rowIndex)
                                      : props.expanderConfig.expanderTooltip ?? (isExpanded ? "Collapse" : "Expand")}
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={e => {
                                        e.stopPropagation();
                                        const expanded = expandedRowIndex === rowIndex ? false : true;
                                        setExpandedRowIndex(expanded ? rowIndex : null);
                                        props.expanderConfig?.onRowExpand?.(row, rowIndex, expanded);
                                      }}
                                      aria-label={isExpanded ? "Collapse" : "Expand"}
                                    >
                                      {isExpanded
                                        ? props.expanderConfig.expanderIcon ?? <KeyboardArrowUpIcon />
                                        : props.expanderConfig.expanderIcon ?? <KeyboardArrowDownIcon />}
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              )}
                              {props.columns.map((col, cidx) => (
                                <React.Fragment key={cidx}>
                                  <DtDataGridCell
                                    key={cidx}
                                    row={row}
                                    col={col}
                                    rowIndex={rowIndex}
                                    colIndex={cidx}
                                    isEditing={isEditing}
                                    editRowData={editRowData}
                                    setEditRowData={setEditRowData}
                                    controlWidth={col.controlWidth}
                                    errors={isEditing ? validationErrors : undefined}
                                    sx={col.sx ?? undefined}
                                    pageConfig={pageConfig}
                                  />
                                  {shouldRenderConnector(cidx) && (
                                    <TableCell
                                      key={`connector-cell-${rowIndex}-${cidx}`}
                                      align="center"
                                      sx={{ width: 32, minWidth: 32, border: 0, background: "transparent", p: 0 }}
                                    >
                                      {props.connectorBetweenColumns?.find(conn => conn.from === cidx)?.connector ?? <ArrowForwardIcon color="action" />}
                                    </TableCell>
                                  )}
                                </React.Fragment>
                              ))}
                              {mergedActionsConfig.actions && mergedActionsConfig.actions.length > 0 && (
                                <DtDataGridActionsCell
                                  actionsConfig={mergedActionsConfig}
                                  row={row}
                                  rowIndex={rowIndex}
                                  isEditing={isEditing}
                                  addMode={false}
                                  editRowData={editRowData}
                                  onEditInit={() => handleEditInit(row, rowIndex)}
                                  onSave={() => {
                                    const errors = validateRow(editRowData);
                                    setValidationErrors(errors);
                                    if (Object.keys(errors).length > 0) return;
                                    if (
                                      props.editConfig?.mode !== "custom" &&
                                      typeof props.editConfig?.onEdit === "function"
                                    ) {
                                      props.editConfig.onEdit(editRowData, rowIndex, "inline");
                                    }
                                    setEditRowIndex(null);
                                    setEditRowData(null);
                                    setValidationErrors({});
                                  }}
                                  onCancel={() => {
                                    setEditRowIndex(null);
                                    setEditRowData(null);
                                    setValidationErrors({});
                                  }}
                                />
                              )}
                            </TableRow>
                            {props.expanderConfig && isExpanded && (
                              <TableRow>
                                <TableCell 
                                  sx={{ 
                                    border: 0, 
                                    p: 0,
                                    width: props.expanderConfig.expanderColumnWidth ?? 40,
                                    backgroundColor: 'transparent'
                                  }}
                                />
                                <TableCell 
                                  colSpan={
                                    props.columns.length + 
                                    (mergedActionsConfig.actions?.length ? 1 : 0)
                                  }
                                  sx={{ 
                                    borderBottom: '1px solid',
                                    borderBottomColor: 'divider',
                                    py: 2,
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                  }}
                                >
                                  <Box>
                                    {props.expanderConfig.renderExpandedRow
                                      ? props.expanderConfig.renderExpandedRow(row, rowIndex)
                                      : <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>No expanded content provided.</Typography>}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {props.addConfig && renderAddRow()}
                    </>
                  )
                }
              </TableBody>
            )
          }
        </Table>
      </TableContainer>
      {props.paged && getSortedItems().length > 0 && (
          <Box mt={1} className="flexRight" sx={{ alignItems: "center", display: "flex", gap: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: 13, mr: 0.5 }}>
              Rows Per Page
            </Typography>
            <FormControl size="small" sx={{ minWidth: 64, maxWidth: 80 }}>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={e => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrPage(1); // Reset to first page on page size change
                }}
                sx={{
                  fontSize: 13,
                  height: 32,
                  '& .MuiSelect-select': {
                    padding: '4px 8px',
                    fontSize: 13,
                    minHeight: 'unset',
                  },
                }}
              >
                {pageSizeOptions.map(opt => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: 13, minHeight: 28 }}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
              <Typography variant="body2" sx={{ fontSize: 13, mr: 0.5 }}>
                Go To Page
              </Typography>

              <TextField
                size="small"
                type="number"
                inputProps={{
                  min: 1,
                  max: Math.max(1, Math.ceil(props.data.length / rowsPerPage)),
                  style: { padding: "7px 8px", fontSize: 13 }
                }}
                value={gotoPage}
                onChange={e => {
                  const val = Number(e.target.value);
                  setGotoPage(e.target.value === "" ? "" : Math.max(1, Math.min(val, Math.ceil(props.data.length / rowsPerPage))));
                }}
                sx={{ width: 104 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" >
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label="go to page"
                        disabled={
                          gotoPage === "" ||
                          gotoPage === currPage ||
                          gotoPage < 1 ||
                          gotoPage > Math.ceil(props.data.length / rowsPerPage)
                        }
                        onClick={() => {
                          if (
                            gotoPage !== "" &&
                            gotoPage >= 1 &&
                            gotoPage <= Math.ceil(props.data.length / rowsPerPage)
                          ) {
                            setCurrPage(Number(gotoPage));
                            if (typeof props.onPageChange === "function") {
                              props.onPageChange(Number(gotoPage));
                            }
                          }
                        }}
                         sx={{ p: 0 }}
                      >
                        <ArrowForwardIcon fontSize="small" sx={{p : 0}}/>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            
            <Pagination
              count={Math.ceil(props.data.length / rowsPerPage)}
              page={currPage}
              onChange={(_e, value) => {
                setCurrPage(value);
                if (typeof props.onPageChange === "function") {
                  props.onPageChange(value);
                }
              }}
            />
          </Box>
        )}
        {/* Confirm Dialog for Delete */}
        {props.deleteConfig?.confirm && (
          <DtConfirmDialog
            maxWidth="xs"
            open={deleteDialogOpen}
            title={props.deleteConfig?.confirmTitle || "Confirm Delete"}
            message={
              props.deleteConfig?.confirmMessage ||
              "Are you sure you want to delete this row?"
            }
            confirmLabel={props.deleteConfig?.confirmYesLabel || "Yes"}
            cancelLabel={props.deleteConfig?.confirmNoLabel || "No"}
            confirmColor="primary"
            onConfirm={() => {
              if (deleteTarget && typeof props.deleteConfig?.onDelete === "function") {
                props.deleteConfig.onDelete(deleteTarget.row, deleteTarget.rowIndex);
                setTimeout(() => {
                  const totalRows = props.data.length - 1;
                  const newPageCount = Math.ceil(totalRows / rowsPerPage);
                  if (currPage > newPageCount && newPageCount > 0) {
                    setCurrPage(newPageCount);
                  }
                }, 0);
              }
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
            onClose={() => {
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
          />
        )}
      </Box>
    </ThemeProvider>
  );
});

export { DtDataGrid };